import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/brand";
import BackToAdminLink from "@/components/BackToAdminLink";
import { Lifebuoy, ArrowClockwise, EnvelopeSimple, Clock, User } from "@phosphor-icons/react";

const COLUMNS = [
  { id: "new", label: "New", accent: "bg-[#FFBD4A]" },
  { id: "in_progress", label: "In progress", accent: "bg-blue-100" },
  { id: "waiting_user", label: "Waiting on user", accent: "bg-purple-100" },
  { id: "done", label: "Done", accent: "bg-emerald-100" },
];

export default function AdminTicketsPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/tickets`);
      setTickets(res.data.tickets || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load tickets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_admin]);

  const move = async (ticketId, newStatus) => {
    // Optimistic UI update.
    setTickets((prev) =>
      prev.map((t) => (t.id === ticketId ? { ...t, status: newStatus } : t)),
    );
    try {
      await axios.patch(`${API}/admin/tickets/${ticketId}`, { status: newStatus });
      toast.success(`Moved to ${newStatus.replace("_", " ")}`);
    } catch (err) {
      toast.error("Failed to update ticket — reloading");
      load();
    }
  };

  const grouped = useMemo(() => {
    const g = { new: [], in_progress: [], waiting_user: [], done: [] };
    tickets.forEach((t) => {
      if (g[t.status]) g[t.status].push(t);
      else g.new.push(t);
    });
    return g;
  }, [tickets]);

  if (user && !user.is_admin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-6" data-testid="admin-tickets-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <BackToAdminLink />
          <div className="section-eyebrow flex items-center gap-2">
            <Lifebuoy size={16} weight="bold" /> Admin · Support
          </div>
          <h1 className="font-heading uppercase text-4xl mt-2">Support tickets</h1>
          <p className="text-black/75 mt-3 leading-relaxed">
            Every customer-submitted ticket lands here, sorted into four columns so you can see
            what needs attention at a glance. Click any card to expand the full message and use
            "Reply by email" to open a pre-filled draft in your default mail app — replies still go
            through your own inbox so the conversation stays on record. Move tickets across the
            board as you work through them: New → In progress → Waiting on user → Done. Everything
            is live for the whole admin team, so if support@ and admin@ are both open, you'll see
            each other's status changes reflected in real time on refresh.
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={load}
          disabled={loading}
          data-testid="admin-tickets-reload"
        >
          <ArrowClockwise size={14} weight="bold" /> {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ticket-kanban">
        {COLUMNS.map((col) => (
          <div key={col.id} className="space-y-3">
            <div className={`p-2 rounded-lg border-[1.5px] border-black flex items-center justify-between ${col.accent}`}>
              <h2 className="font-heading uppercase text-sm tracking-wider">{col.label}</h2>
              <span className="text-xs font-semibold">{grouped[col.id].length}</span>
            </div>
            <div className="space-y-3 min-h-[200px]">
              {grouped[col.id].length === 0 ? (
                <p className="text-xs text-black/40 italic text-center pt-4">Nothing here.</p>
              ) : (
                grouped[col.id].map((t) => (
                  <TicketCard key={t.id} ticket={t} onMove={move} />
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TicketCard({ ticket, onMove }) {
  const [expanded, setExpanded] = useState(false);
  const created = new Date(ticket.created_at);
  return (
    <div
      className="card !p-3 hover:bg-[#FFF7E5] transition-colors cursor-pointer"
      onClick={() => setExpanded((v) => !v)}
      data-testid={`ticket-card-${ticket.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold truncate">{ticket.subject}</h3>
          <div className="flex items-center gap-2 text-[10px] text-black/55 mt-1 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <User size={10} weight="bold" />
              {ticket.user_name || ticket.user_email}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock size={10} weight="bold" />
              {created.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
            </span>
            <span className="uppercase tracking-widest font-heading bg-black/5 border border-black/10 rounded px-1">
              {ticket.category}
            </span>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="mt-3 pt-3 border-t border-black/10" onClick={(e) => e.stopPropagation()}>
          <p className="text-xs text-black/75 whitespace-pre-wrap leading-relaxed">
            {ticket.message}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <a
              href={`mailto:${ticket.user_email}?subject=Re: ${encodeURIComponent(ticket.subject)}`}
              className="btn-secondary !px-2 !py-1 !text-xs"
              data-testid={`ticket-reply-${ticket.id}`}
            >
              <EnvelopeSimple size={12} weight="bold" /> Reply by email
            </a>
            <div className="flex gap-1 ml-auto">
              {COLUMNS.filter((c) => c.id !== ticket.status).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onMove(ticket.id, c.id)}
                  className="!px-2 !py-1 !text-[10px] uppercase tracking-widest font-heading rounded border-[1.5px] border-black bg-white hover:bg-[#FFBD4A]"
                  data-testid={`ticket-move-${ticket.id}-${c.id}`}
                >
                  → {c.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
